/** @jsxImportSource react */
import { usePrefetch } from '@react/services/api';
import { pageNames, useEvents } from '@react/services/events';
import BoxedSection from '@react/shared/components/BoxedSection';
import ToolCardList from '@react/tools/components/ToolCardList';
import {
  tranformSeniorLivingTypes,
  transformChecklists,
  transformSeniorLivingTools,
} from '@sly/frontend/common/helpers/tools';
import { ToolLayout } from '@sly/frontend/react/tools/components/Styles';
import Helmet from 'next/head';
import { StrictMode, useEffect } from 'react';

import CarePlans from './CarePlans';
import CareScoutCostOfCareWidget from './CareScoutCostOfCareWidget';

export default function ToolPage() {
  const { events } = useEvents();

  const {
    requestInfo: { hasFinished, result: toolsPageResult },
  } = usePrefetch('getToolsPage', {
    'populate[seniorLivingTools][populate]': '*',
    'populate[checklists][populate]': 'mainImage',
    'populate[seniorLivingTypes][populate]': 'mainImg',
  });

  useEffect(() => {
    events.page(pageNames.Tools, {});
  }, []);

  const toolsPageData = toolsPageResult?.data;

  if ((hasFinished && !toolsPageData) || !hasFinished) return null;

  const touringChecklist = transformChecklists(toolsPageData?.checklists || []);
  const seniorLivingTools = transformSeniorLivingTools(
    toolsPageData?.seniorLivingTools || []
  );

  const seniorLivingTypes = tranformSeniorLivingTypes(
    toolsPageData?.seniorLivingTypes || []
  );

  const title = toolsPageData?.title || 'Senior living tools and resources';
  const description = toolsPageData?.description;

  return (
    <StrictMode>
      <Helmet>
        <title>Senior Living Tools - Seniorly</title>
        <meta
          name='description'
          content="Discover guides, tips, and other resources to help you learn about senior living with Seniorly's comprehensive tools."
        />
      </Helmet>
      <ToolLayout
        breadCrumbItems={[
          {
            label: 'Home',
            path: '/',
          },
          {
            label: 'Senior living tools',
            path: '/tools',
          },
        ]}
      >
        <BoxedSection id='tools' className='container'>
          <h1 className='font-t-l mb-2 font-bold'>{title}</h1>
          {!!description && (
            <div
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          )}
        </BoxedSection>
        <BoxedSection id='care-plans' className='container'>
          <CarePlans />
        </BoxedSection>

        <BoxedSection id='cost-of-care' className='container'>
          <CareScoutCostOfCareWidget />
        </BoxedSection>

        {!!seniorLivingTools?.length && (
          <BoxedSection
            heading='Calculators and quizzes'
            id='calculator-and-quizzes'
            className='container'
          >
            <ToolCardList items={seniorLivingTools} />
          </BoxedSection>
        )}

        {!!touringChecklist?.length && (
          <BoxedSection
            heading='Touring checklists'
            id='touring-checklists'
            className='container'
          >
            <ToolCardList items={touringChecklist} />
          </BoxedSection>
        )}

        {!!seniorLivingTypes?.length && (
          <BoxedSection
            heading='Senior living guides'
            id='senior-living-guides'
            className='container'
          >
            <ToolCardList items={seniorLivingTypes} />
          </BoxedSection>
        )}
      </ToolLayout>
    </StrictMode>
  );
}
